import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";


function Search() {
  const { type, name } = useParams();
  const navigate = useNavigate();

  const decodedName = name ? decodeURIComponent(name) : "";

  const [searchType, setSearchType] = useState(type || "city");
  const [searchInput, setSearchInput] = useState(decodedName);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [sortConfig, setSortConfig] = useState({
    key: "marketCap",
    direction: "desc",
  });


  // ------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------

  const toNum = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  };


  const formatNumber = (value) =>
    toNum(value).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    });


  // Market cap received in Rupees
  // Convert to Crores
  const formatMarketCap = (value) => {
    const n = toNum(value);

    if (!n) {
      return "—";
    }

    const crore = n / 10000000;

    return `${crore.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })} Cr`;
  };


  const calcPct = (current, base) => {
    current = toNum(current);
    base = toNum(base);

    return current && base
      ? ((current - base) / base) * 100
      : null;
  };


  const formatPct = (value) =>
    Number.isFinite(value)
      ? `${value.toFixed(2)}%`
      : "—";


  const pctClass = (value) => {
    if (!Number.isFinite(value)) {
      return "text-gray-500";
    }

    if (value > 0) {
      return "text-green-600";
    }

    if (value < 0) {
      return "text-red-600";
    }

    return "text-gray-600";
  };


  const typeLabel =
    searchType === "holding"
      ? "Holding"
      : searchType === "investor"
      ? "Investor"
      : searchType === "subject"
      ? "Subject"
      : "City";


  // ------------------------------------------------------------
  // Industry labels
  // ------------------------------------------------------------

const renderIndustry = (industry) => {
  if (!industry) {
    return "—";
  }

  const industryValue = String(industry).trim();

  // Show "-" for NEW or N
  if (["NEW", "N"].includes(industryValue.toUpperCase())) {
    return "-";
  }

  const industries = industryValue
    .split("/")
    .map((item) => item.trim())
    .filter(Boolean);

    if (industries.length === 0) {
      return "—";
    }

    return (
      <div className="flex flex-col items-start gap-1">
        {industries.map((item, index) => (
          <span
            key={`${item}-${index}`}
            className="inline-block px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-700 whitespace-nowrap"
          >
            {item}
          </span>
        ))}
      </div>
    );
  };


  // ------------------------------------------------------------
  // Sync URL -> search controls
  // ------------------------------------------------------------

  useEffect(() => {
    setSearchType(type || "city");
    setSearchInput(decodedName);

    if (!name) {
      setData([]);
      setError("");
    }
  }, [type, decodedName, name]);


  // ------------------------------------------------------------
  // Fetch search result
  // ------------------------------------------------------------

  useEffect(() => {
    if (!type || !name) {
      return;
    }

    setLoading(true);
    setError("");

    axios
      .get(
        `${process.env.REACT_APP_API_URL}/api/search/${encodeURIComponent(
          type
        )}/${encodeURIComponent(decodedName)}`
      )
      .then((res) => {
        if (type === "investor") {
          setData(
            Array.isArray(res.data?.results)
              ? res.data.results
              : []
          );
        } else {
          setData(
            Array.isArray(res.data)
              ? res.data
              : []
          );
        }
      })
      .catch((err) => {
        console.error("Search failed:", err);

        setData([]);

        setError(
          err?.response?.data?.error ||
            `Failed to search ${type}: ${decodedName}`
        );
      })
      .finally(() => {
        setLoading(false);
      });

  }, [type, name, decodedName]);


  // ------------------------------------------------------------
  // Search
  // ------------------------------------------------------------

  const handleSearch = () => {
    const value = searchInput.trim();

    if (!value) {
      navigate("/search");
      return;
    }

    navigate(
      `/search/${searchType}/${encodeURIComponent(value)}`
    );
  };


  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };


  // ------------------------------------------------------------
  // Sorting
  // ------------------------------------------------------------

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key &&
        prev.direction === "desc"
          ? "asc"
          : "desc",
    }));
  };


  const sortIcon = (key) => {
    if (sortConfig.key !== key) {
      return "↕";
    }

    return sortConfig.direction === "asc"
      ? "↑"
      : "↓";
  };


  const sortedData = useMemo(() => {
    const rows = [...data];

    rows.sort((a, b) => {
      let A;
      let B;


      // ----------------------------
      // Calculated fields
      // ----------------------------

      if (sortConfig.key === "change") {

        A = calcPct(
          a.currentPrice,
          a.previousClose
        );

        B = calcPct(
          b.currentPrice,
          b.previousClose
        );

      }

      else if (sortConfig.key === "dma50Diff") {

        A = calcPct(
          a.currentPrice,
          a.fiftyDayAverage
        );

        B = calcPct(
          b.currentPrice,
          b.fiftyDayAverage
        );

      }

      else if (sortConfig.key === "dma200Diff") {

        A = calcPct(
          a.currentPrice,
          a.twoHundredDayAverage
        );

        B = calcPct(
          b.currentPrice,
          b.twoHundredDayAverage
        );

      }

      else if (sortConfig.key === "fiftyTwoWeekHighDiff") {

        A = calcPct(
          a.currentPrice,
          a.fiftyTwoWeekHigh
        );

        B = calcPct(
          b.currentPrice,
          b.fiftyTwoWeekHigh
        );

      }

      else {

        A = a?.[sortConfig.key];
        B = b?.[sortConfig.key];

      }


      // ----------------------------
      // String sorting
      // ----------------------------

      if (
        ["name", "industry"].includes(
          sortConfig.key
        )
      ) {

        A = String(A || "").toLowerCase();
        B = String(B || "").toLowerCase();

        return sortConfig.direction === "asc"
          ? A.localeCompare(B)
          : B.localeCompare(A);
      }


      // ----------------------------
      // Numeric sorting
      // ----------------------------

      A = parseFloat(A);
      B = parseFloat(B);

      if (!Number.isFinite(A)) {
        return 1;
      }

      if (!Number.isFinite(B)) {
        return -1;
      }

      return sortConfig.direction === "asc"
        ? A - B
        : B - A;
    });

    return rows;

  }, [data, sortConfig]);


const SortHeader = ({ label, sortKey }) => {
  const isActive = sortConfig.key === sortKey;

  return (
    <th
      onClick={() => requestSort(sortKey)}
      className={`p-2 text-right cursor-pointer select-none whitespace-nowrap hover:bg-gray-300 ${
        isActive ? "text-blue-600 font-semibold" : ""
      }`}
    >
      {label}{" "}
      <span className={isActive ? "text-blue-600" : "text-gray-500"}>
        {sortIcon(sortKey)}
      </span>
    </th>
  );
};


  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <div className="p-4 mb-4">

      <div className="mb-3">

        <h1 className="text-xl font-semibold text-gray-800">
          Smart Search
        </h1>

        <div className="text-xs text-gray-500 mt-1">
          Search stocks by city or holding Group.
        </div>

      </div>


      {/* --------------------------------------------------------
          Search type
      -------------------------------------------------------- */}

      <div className="flex items-center gap-4 mb-2 text-sm">

        <label className="flex items-center gap-1 cursor-pointer">

          <input
            type="radio"
            name="searchType"
            value="city"
            checked={searchType === "city"}
            onChange={() =>
              setSearchType("city")
            }
          />

          City

        </label>


        <label className="flex items-center gap-1 cursor-pointer">

          <input
            type="radio"
            name="searchType"
            value="holding"
            checked={searchType === "holding"}
            onChange={() =>
              setSearchType("holding")
            }
          />

          Holding Group

        </label>

        <label className="flex items-center gap-1 cursor-pointer">
          <input
            type="radio"
            name="searchType"
            value="investor"
            checked={searchType === "investor"}
            onChange={() => setSearchType("investor")}
          />

          Investor
        </label>

        <label className="flex items-center gap-1 cursor-pointer">
          <input
            type="radio"
            name="searchType"
            value="subject"
            checked={searchType === "subject"}
            onChange={() => setSearchType("subject")}
          />

          Subject
        </label>

      </div>


      {/* --------------------------------------------------------
          Search box
      -------------------------------------------------------- */}

      <div className="flex items-center gap-2 flex-wrap mb-3">

        <input
          type="text"
          value={searchInput}
          onChange={(e) =>
            setSearchInput(e.target.value)
          }
          onKeyDown={handleKeyDown}
          placeholder={
            searchType === "holding"
              ? "Enter holding e.g. Tata"
              : searchType === "investor"
              ? "Enter investor e.g. Catamaran"
              : searchType === "subject"
              ? "Enter subject e.g. Semiconductor"
              : "Enter city e.g. Mumbai"
          }
          className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full sm:w-64 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />


        <button
          onClick={handleSearch}
          className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
        >
          Search
        </button>

      </div>


      {/* --------------------------------------------------------
          Summary
      -------------------------------------------------------- */}

      {name && !loading && (

        <div className="mb-3 text-sm text-gray-600">

          {typeLabel}:{" "}

          <span className="font-semibold text-gray-800">
            {decodedName}
          </span>


          <span className="ml-3">

            {searchType === "investor"
              ? "Total deals: "
              : searchType === "subject"
              ? "Total news: "
              : "Total stocks: "}

            <span className="font-semibold text-gray-800">
              {sortedData.length}
            </span>

          </span>

        </div>

      )}


      {/* --------------------------------------------------------
          Loading
      -------------------------------------------------------- */}

      {loading && (

        <div className="text-center text-blue-600 font-medium py-8 animate-pulse">
          Loading...
        </div>

      )}


      {/* --------------------------------------------------------
          Error
      -------------------------------------------------------- */}

      {!loading && error && (

        <div className="text-sm text-red-600 py-4">
          {error}
        </div>

      )}

      {/* Investor Results */}

      {!loading &&
        !error &&
        type === "investor" &&
        name && (

        <div className="overflow-x-auto">

          <table className="border-collapse min-w-max w-full text-sm text-gray-800">

            <thead>
              <tr className="bg-gray-200">

                <th className="p-2 text-left">#</th>

                <th className="p-2 text-left">
                  Investor
                </th>

                <th className="p-2 text-left">
                  Type
                </th>

                <th className="p-2 text-left">
                  Company
                </th>

                <th className="p-2 text-left">
                  Date
                </th>

                <th className="p-2 text-left">
                  Buy / Sell
                </th>

                <th className="p-2 text-right">
                  Quantity
                </th>

                <th className="p-2 text-right">
                  Price
                </th>

              </tr>
            </thead>


            <tbody>

              {data.length === 0 ? (

                <tr>
                  <td
                    colSpan="8"
                    className="text-center text-gray-500 py-6"
                  >
                    No investor deals found for {decodedName}.
                  </td>
                </tr>

              ) : (

                data.map((row, index) => {

                  const symbol =
                    row.symbol ||
                    row.Symbol ||
                    "";

                  return (

                    <tr
                      key={`${row.dealType}-${symbol}-${row.date}-${index}`}
                      className={
                        index % 2 === 0
                          ? "bg-white"
                          : "bg-gray-100"
                      }
                    >

                      <td className="p-2 text-gray-500">
                        {index + 1}
                      </td>


                      <td className="p-2 font-medium whitespace-nowrap">
                        {row.investorName || "—"}
                      </td>


                      <td className="p-2 whitespace-nowrap">
                        {row.dealType || "—"}
                      </td>


                      <td className="p-2 whitespace-nowrap">

                        {symbol ? (
                          <a
                            href={`/symbol/${symbol}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {row.companyName ||
                              row.company ||
                              symbol}
                          </a>
                        ) : (
                          row.companyName ||
                          row.company ||
                          "—"
                        )}

                      </td>


                      <td className="p-2 whitespace-nowrap">
                        {row.date || "—"}
                      </td>


                      <td className="p-2 whitespace-nowrap">
                        {row.buySell ||
                          row.buySellType ||
                          row.acquisitionMode ||
                          "—"}
                      </td>


                      <td className="p-2 text-right">
                        {row.quantity
                          ? formatNumber(row.quantity)
                          : "—"}
                      </td>


                      <td className="p-2 text-right">
                        {row.price
                          ? formatNumber(row.price)
                          : "—"}
                      </td>

                    </tr>

                  );
                })

              )}

            </tbody>

          </table>

        </div>
      )}


      {/* Subject / News Results */}

      {!loading &&
        !error &&
        type === "subject" &&
        name && (

        <div className="overflow-x-auto">

          <table className="border-collapse min-w-max w-full text-sm text-gray-800">

            <thead>
              <tr className="bg-gray-200">

                <th className="p-2 text-left">
                  #
                </th>

                <th className="p-2 text-left">
                  Date
                </th>

                <th className="p-2 text-left">
                  Type
                </th>

                <th className="p-2 text-left">
                  Source
                </th>

                <th className="p-2 text-left">
                  Title
                </th>

                <th className="p-2 text-left">
                  Summary
                </th>

              </tr>
            </thead>


            <tbody>

              {data.length === 0 ? (

                <tr>
                  <td
                    colSpan="6"
                    className="text-center text-gray-500 py-6"
                  >
                    No news found for {decodedName}.
                  </td>
                </tr>

              ) : (

                data.map((row, index) => (

                  <tr
                    key={`${row.title_hash || row.link}-${index}`}
                    className={
                      index % 2 === 0
                        ? "bg-white"
                        : "bg-gray-100"
                    }
                  >

                    {/* # */}

                    <td className="p-2 text-gray-500 align-top">
                      {index + 1}
                    </td>


                    {/* Date */}

                    <td className="p-2 whitespace-nowrap align-top">
                      {row.published || "—"}
                    </td>


                    {/* News Type */}

                    <td className="p-2 whitespace-nowrap align-top">

                      <span className="inline-block px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-700">
                        {row.newsType || "—"}
                      </span>

                    </td>


                    {/* Source */}

                    <td className="p-2 whitespace-nowrap align-top">
                      {row.source || "—"}
                    </td>


                    {/* Title */}

                    <td className="p-2 font-medium align-top max-w-md">

                      {row.link ? (

                        <a
                          href={row.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {row.title || "—"}
                        </a>

                      ) : (

                        row.title || "—"

                      )}

                    </td>


                    {/* Summary */}

                    <td className="p-2 text-gray-600 align-top max-w-xl whitespace-normal">
                      {row.summary || "—"}
                    </td>

                  </tr>

                ))

              )}

            </tbody>

          </table>

        </div>
      )}


      {/* --------------------------------------------------------
          Results table
      -------------------------------------------------------- */}

      {!loading &&
        !error &&
        type &&
        type !== "investor" &&
        type !== "subject" &&
        name && (

        <div className="overflow-x-auto">

          <table className="border-collapse min-w-max w-full text-sm text-gray-800">

            <thead>

              <tr className="bg-gray-200">

                <th className="p-2 text-left">
                  #
                </th>


                {/* Name now replaces Symbol */}

<th
  onClick={() => requestSort("name")}
  className={`p-2 text-left cursor-pointer sticky left-0 bg-gray-200 z-10 whitespace-nowrap ${
    sortConfig.key === "name"
      ? "text-blue-600 font-semibold"
      : ""
  }`}
>
  Name{" "}
  <span
    className={
      sortConfig.key === "name"
        ? "text-blue-600"
        : "text-gray-500"
    }
  >
    {sortIcon("name")}
  </span>
</th>

<th
  onClick={() => requestSort("industry")}
  className={`p-2 text-left cursor-pointer whitespace-nowrap ${
    sortConfig.key === "industry"
      ? "text-blue-600 font-semibold"
      : ""
  }`}
>
  Industry{" "}
  <span
    className={
      sortConfig.key === "industry"
        ? "text-blue-600"
        : "text-gray-500"
    }
  >
    {sortIcon("industry")}
  </span>
</th>


                <SortHeader
                  label="MCap"
                  sortKey="marketCap"
                />

                <SortHeader
                  label="Price"
                  sortKey="currentPrice"
                />

                <SortHeader
                  label="Chg%"
                  sortKey="change"
                />


                <SortHeader
                  label="Del%"
                  sortKey="delivery_pc"
                />


                <SortHeader
                  label="50 DMA"
                  sortKey="fiftyDayAverage"
                />

                <SortHeader
                  label="50 Diff"
                  sortKey="dma50Diff"
                />


                <SortHeader
                  label="200 DMA"
                  sortKey="twoHundredDayAverage"
                />

                <SortHeader
                  label="200 Diff"
                  sortKey="dma200Diff"
                />


                <SortHeader
                  label="52W High"
                  sortKey="fiftyTwoWeekHigh"
                />

                <SortHeader
                  label="52WH Diff"
                  sortKey="fiftyTwoWeekHighDiff"
                />

                <SortHeader
                  label="52W Low"
                  sortKey="fiftyTwoWeekLow"
                />


                <SortHeader
                  label=">2% / 10D"
                  sortKey="last10days_morethan_2pc"
                />

              </tr>

            </thead>


            <tbody>

              {sortedData.length === 0 ? (

                <tr>

                  <td
                    colSpan="15"
                    className="text-center text-gray-500 py-6"
                  >
                    No stocks found for {decodedName}.
                  </td>

                </tr>

              ) : (

                sortedData.map(
                  (row, index) => {

                    const change = calcPct(
                      row.currentPrice,
                      row.previousClose
                    );


                    const dma50Diff = calcPct(
                      row.currentPrice,
                      row.fiftyDayAverage
                    );


                    const dma200Diff = calcPct(
                      row.currentPrice,
                      row.twoHundredDayAverage
                    );


                    const fiftyTwoWeekHighDiff =
                      calcPct(
                        row.currentPrice,
                        row.fiftyTwoWeekHigh
                      );


                    const bg =
                      index % 2 === 0
                        ? "bg-white"
                        : "bg-gray-100";


                    return (

                      <tr
                        key={row.symbol}
                        className={bg}
                      >

                        {/* # */}

                        <td className="p-2 text-gray-500">
                          {index + 1}
                        </td>


                        {/* Name + hyperlink */}

                        <td
                          className={`p-2 font-medium sticky left-0 z-10 ${bg}`}
                        >

                          <a
                            href={`/symbol/${row.symbol}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline whitespace-nowrap"
                          >
                            {row.name || row.symbol || "—"}
                          </a>

                        </td>


                        {/* Industry labels */}

                        <td className="p-2 align-top">
                          {renderIndustry(
                            row.industry
                          )}
                        </td>


                        {/* Market Cap in Cr */}

                        <td className="p-2 text-right whitespace-nowrap">
                          {formatMarketCap(
                            row.marketCap
                          )}
                        </td>


                        {/* Price */}

                        <td className="p-2 text-right">
                          {formatNumber(
                            row.currentPrice
                          )}
                        </td>


                        {/* Change */}

                        <td
                          className={`p-2 text-right font-medium ${pctClass(
                            change
                          )}`}
                        >
                          {formatPct(change)}
                        </td>


                        {/* Delivery % */}

                        <td className="p-2 text-right">
                          {Number.isFinite(
                            parseFloat(
                              row.delivery_pc
                            )
                          )
                            ? `${formatNumber(
                                row.delivery_pc
                              )}%`
                            : "—"}
                        </td>


                        {/* 50 DMA */}

                        <td className="p-2 text-right">
                          {formatNumber(
                            row.fiftyDayAverage
                          )}
                        </td>


                        {/* 50 DMA Diff */}

                        <td
                          className={`p-2 text-right ${pctClass(
                            dma50Diff
                          )}`}
                        >
                          {formatPct(
                            dma50Diff
                          )}
                        </td>


                        {/* 200 DMA */}

                        <td className="p-2 text-right">
                          {formatNumber(
                            row.twoHundredDayAverage
                          )}
                        </td>


                        {/* 200 DMA Diff */}

                        <td
                          className={`p-2 text-right ${pctClass(
                            dma200Diff
                          )}`}
                        >
                          {formatPct(
                            dma200Diff
                          )}
                        </td>


                        {/* 52W High */}

                        <td className="p-2 text-right">
                          {formatNumber(
                            row.fiftyTwoWeekHigh
                          )}
                        </td>


                        {/* 52W High Diff */}

                        <td
                          className={`p-2 text-right font-medium ${pctClass(
                            fiftyTwoWeekHighDiff
                          )}`}
                        >
                          {formatPct(
                            fiftyTwoWeekHighDiff
                          )}
                        </td>


                        {/* 52W Low */}

                        <td className="p-2 text-right">
                          {formatNumber(
                            row.fiftyTwoWeekLow
                          )}
                        </td>


                        {/* >2% / 10D */}

                        <td className="p-2 text-right font-medium">
                          {formatNumber(
                            row.last10days_morethan_2pc
                          )}
                        </td>

                      </tr>

                    );
                  }
                )

              )}

            </tbody>

          </table>

        </div>

      )}

    </div>
  );
}


export default Search;